import {Alert, Dialog, Segment} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';

import type {FinancialProductsResponseBody, FinancialProductType} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {useFinancialProductPreferenceEditor} from '../hooks/useFinancialProductPreferenceEditor';
import {FinancingPreferenceEditor} from './FinancingPreferenceEditor';
import {LeasingPreferenceEditor} from './LeasingPreferenceEditor';

interface FinancialProductPreferenceDialogProps {
  businessCaseId: string;
  products: FinancialProductsResponseBody;
  vehiclePrice: {amount: number; currency: string};
  canUpdate: boolean;
  onClose: () => void;
}

function productLabel(type: FinancialProductType) {
  return i18n.t(
    {
      STANDARD: 'page.vehicle.financingAndLeasing.standard',
      BALLOON: 'page.vehicle.financingAndLeasing.balloon',
      LEASING: 'financialProducts.product.leasing',
    }[type]
  );
}

export function FinancialProductPreferenceDialog(props: FinancialProductPreferenceDialogProps) {
  const editor = useFinancialProductPreferenceEditor(props);
  const isDisabled = !editor.isWritable || editor.isSaving;
  const offer = editor.activeOffer;
  const actor = match(editor.state.baseline.preferenceUpdatedBy)
    .with({source: 'customer'}, () =>
      i18n.t('page.businessCase.financialProducts.updatedByCustomer')
    )
    .with({source: 'seller', seller: Pattern.nonNullable}, ({seller}) =>
      i18n.t('page.businessCase.financialProducts.updatedBySeller', {seller: seller.displayName})
    )
    .with({source: 'seller'}, () =>
      i18n.t('page.businessCase.financialProducts.updatedByRemovedSeller')
    )
    .otherwise(() => null);

  return (
    <Dialog
      isOpen
      size="large"
      title={i18n.t('page.businessCase.financialProducts.customerPreference')}
      onClose={editor.close}
      buttons={[
        {
          title: i18n.t('general.actions.cancel'),
          variant: 'secondary',
          onClick: editor.close,
          isDisabled: editor.isSaving,
        },
        {
          title: i18n.t('general.actions.save'),
          variant: 'primary',
          onClick: editor.save,
          isDisabled: !editor.canSave,
          isLoading: editor.isSaving,
        },
      ]}
    >
      <VStack spacing={4} align="stretch">
        <FinancialProductPreferenceTabs editor={editor} isDisabled={isDisabled} />
        <Show when={editor.state.saveError === 'conflict'}>
          <Alert
            type="inline"
            variant="warning"
            title={i18n.t('page.businessCase.financialProducts.saveConflict')}
          />
          <Text size="small" color="secondary">
            {actor}
          </Text>
        </Show>
        <Show when={!editor.isWritable}>
          <Alert
            type="inline"
            variant="warning"
            title={i18n.t(
              editor.state.baseline.mode === 'CONTRACT_SNAPSHOT' ||
                props.products.mode === 'CONTRACT_SNAPSHOT'
                ? 'page.businessCase.financialProducts.signedValues'
                : 'financialProducts.unavailable.generic'
            )}
          />
        </Show>
        <Show when={editor.state.saveError === 'failed'}>
          <Alert
            type="inline"
            variant="error"
            title={i18n.t('general.notifications.changesSavingFailed')}
          />
        </Show>
        {isNil(offer) ? (
          <Text color="secondary">{i18n.t('page.businessCase.financialProducts.noProducts')}</Text>
        ) : (
          match(editor.activeCoordinates)
            .with({productType: Pattern.union('STANDARD', 'BALLOON')}, (coordinates) => (
              <FinancingPreferenceEditor
                type={coordinates.productType}
                offer={offer}
                coordinates={coordinates}
                displayed={editor.activeDisplayed}
                vehiclePrice={props.vehiclePrice}
                isDisabled={isDisabled}
                onChange={editor.changeCoordinates}
              />
            ))
            .with({productType: 'LEASING'}, (coordinates) => (
              <LeasingPreferenceEditor
                data-testid="leasingPreference"
                offer={offer}
                coordinates={coordinates}
                displayed={editor.activeDisplayed}
                isDisabled={isDisabled}
                onChange={editor.changeCoordinates}
              />
            ))
            .otherwise(() => (
              <Text color="secondary">{i18n.t('financialProducts.unavailable.generic')}</Text>
            ))
        )}
      </VStack>
    </Dialog>
  );
}

function FinancialProductPreferenceTabs(props: {
  editor: Pick<
    ReturnType<typeof useFinancialProductPreferenceEditor>,
    'state' | 'configuredProducts' | 'changeProduct'
  >;
  isDisabled: boolean;
}) {
  const selectedProduct = props.editor.state.selectedProduct;
  if (isNil(selectedProduct)) {
    return null;
  }
  return (
    <Segment<FinancialProductType>
      label={i18n.t('page.vehicle.financingAndLeasing.calculator')}
      value={selectedProduct}
      options={props.editor.configuredProducts.map((type) => ({
        value: type,
        label: productLabel(type),
      }))}
      isDisabled={props.isDisabled}
      onChange={props.editor.changeProduct}
    />
  );
}
